import axios from 'axios';
import { normalizeApiError } from '../api/errors';

// IMPORTANT: this value is used by the browser.
// Default to same-origin so it works on phones/domains behind a reverse proxy.
const API_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');

const isProbablyJwt = (value) => {
  if (!value) return false;
  if (typeof value !== 'string') return false;
  const token = value.trim();
  // Reject common bad values that are still "truthy" strings.
  if (!token || token === 'undefined' || token === 'null') return false;
  return token.split('.').length === 3;
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies in cross-domain requests
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    // Some endpoints are intentionally public; sending an expired/invalid token
    // can cause a 401 even though the endpoint allows anonymous access.
    const noAuthPaths = [
      '/loans/types/',
      '/auth/login/',
      '/auth/otp/send-password-reset-otp/',
      '/auth/otp/verify-password-reset-otp/',
    ];
    const requestUrl = config.url || '';
    const isNoAuthRequest = noAuthPaths.some((p) => requestUrl.startsWith(p));

    const token = localStorage.getItem('access_token');
    if (isProbablyJwt(token) && !isNoAuthRequest) {
      config.headers.Authorization = `Bearer ${token.trim()}`;
    }
    // If the body is FormData, allow the browser to set Content-Type (with boundary)
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      // Remove any Content-Type header so browser can add correct multipart boundary
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
      if (config.headers && config.headers.common && config.headers.common['Content-Type']) {
        delete config.headers.common['Content-Type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isLoginRequest = originalRequest?.url?.endsWith('/auth/login/');

    // If error is 401 and we haven't tried to refresh token yet
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isLoginRequest
    ) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!isProbablyJwt(refreshToken)) {
          throw new Error('Missing refresh token');
        }

        const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
          refresh: refreshToken.trim(),
        });

        const { access, refresh } = response.data;
        if (access) localStorage.setItem('access_token', access);
        if (refresh) localStorage.setItem('refresh_token', refresh);

        // Retry the original request with new token
        if (access) {
          originalRequest.headers.Authorization = `Bearer ${access}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Attach normalised error for consistent handling in all callers
    error._normalized = normalizeApiError(error);
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  register: (data) => api.post('/auth/register/', data),
  login: (data) => api.post('/auth/login/', data),
  getProfile: () => api.get('/auth/me/'),
  updateProfile: (data) => api.patch('/auth/me/', data),

  // Security
  getSecurityOverview: () => api.get('/auth/security/overview/'),
  updateSecurityPreferences: (data) => api.patch('/auth/security/preferences/', data),
  changePassword: (data) => api.post('/auth/security/password/change/', data),
  setupTwoFactor: () => api.post('/auth/security/2fa/setup/', {}),
  enableTwoFactor: (data) => api.post('/auth/security/2fa/enable/', data),
  disableTwoFactor: (data) => api.post('/auth/security/2fa/disable/', data),
  listSessions: () => api.get('/auth/security/sessions/'),
  revokeSession: (sessionId) => api.post(`/auth/security/sessions/${sessionId}/revoke/`, {}),
  revokeOtherSessions: (data) => api.post('/auth/security/sessions/revoke-others/', data),
  recentActivity: () => api.get('/auth/security/activity/'),

  // Phone verification registration flow
  sendPhoneVerificationCode: (data) => api.post('/auth/send-verification-code/', data),
  verifyPhoneCodeAndRegister: (data) => api.post('/auth/verify-phone-code/', data),

  // Email verification
  sendEmailVerification: () => api.post('/auth/send-email-verification/'),
  resendEmailVerification: () => api.post('/auth/resend-email-verification/'),
  verifyEmailToken: (token) => api.get(`/auth/verify-email/${token}/`),

  // Email verification (OTP)
  sendEmailVerificationCode: (data) => api.post('/auth/send-email-verification-code/', data),
  verifyEmailCode: (data) => api.post('/auth/verify-email-code/', data),

  // Password reset (OTP flow)
  sendPasswordResetOTP: (data) => api.post('/auth/otp/send-password-reset-otp/', data),
  verifyPasswordResetOTP: (data) => api.post('/auth/otp/verify-password-reset-otp/', data),
  resetPasswordWithToken: (data) => api.post('/auth/password-reset/confirm/', data),

  // Verification status + documents
  getVerificationStatus: () => api.get('/auth/verification-status/'),
  getDocumentVerificationStatus: () => api.get('/auth/document-verification-status/'),
  uploadVerificationDocument: ({ upload_type, file }) => {
    const formData = new FormData();
    formData.append('upload_type', upload_type);
    formData.append('file', file);

    // Let the browser set the Content-Type (including boundary)
    return api.post('/auth/verification/upload/', formData);
  },
  uploadDocument: ({ document_type, document_number, document_front, document_back }) => {
    const formData = new FormData();
    formData.append('document_type', document_type);
    formData.append('document_number', document_number);
    formData.append('document_front', document_front);
    if (document_back) formData.append('document_back', document_back);
    return api.post('/auth/upload-document/', formData);
  },
};

// Account services
export const accountService = {
  getAccounts: () => api.get('/accounts/'),
  getAccount: (id) => api.get(`/accounts/${id}/`),
  getTransactions: (accountId, params = {}) => api.get(`/accounts/${accountId}/transactions/`, { params }),
  createAccount: (data) => api.post('/accounts/', data),
  lookupAccount: (accountNumber) => api.get('/accounts/lookup/', { params: { account_number: accountNumber } }),
};

// Transaction services
export const transactionService = {
  getTransactions: (params) => api.get('/transactions/', { params }),
  getTransaction: (id) => api.get(`/transactions/${id}/`),
  generateReceipt: (data) => api.post('/transactions/generate_receipt/', data),
  transfer: (data) => api.post('/transactions/transfer/', data),
  deposit: (data) => api.post('/transactions/deposit/', data),
  withdraw: (data) => api.post('/transactions/withdraw/', data),
  getStatementOfAccount: (data) => api.post('/transactions/statement_of_account/', data),
  getCountries: () => api.get('/transactions/countries/'),
  getBanks: (params) => api.get('/transactions/banks/', { params }),
  validateBankDetails: (data) => api.post('/transactions/validate_bank_details/', data),
  verifyAuthorizationCode: (data) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    if (isFormData) {
      return api.post('/transactions/verify_authorization_code/', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post('/transactions/verify_authorization_code/', data);
  },
  getAuthorizationCodes: () => api.get('/transactions/authorization-codes/'),
  createAuthorizationCode: (data) => api.post('/transactions/authorization-codes/', data),
  revokeAuthorizationCode: (id) => api.post(`/transactions/authorization-codes/${id}/revoke/`, {}),

  downloadReceipt: async ({ transactionId, reference, format = 'pdf' }) => {
    if (!transactionId) throw new Error('transactionId is required');
    const normalizedFormat = String(format || 'pdf').toLowerCase();

    if (normalizedFormat === 'json') {
      const res = await api.get('/transactions/download_receipt/', {
        params: { transaction_id: transactionId, format: 'json' },
      });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${reference || transactionId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return res;
    }

    const responseType = 'blob';
    const res = await api.get('/transactions/download_receipt/', {
      params: { transaction_id: transactionId, format: normalizedFormat },
      responseType,
    });

    const mimeType = normalizedFormat === 'html' ? 'text/html' : 'application/pdf';
    const blob = new Blob([res.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    if (normalizedFormat === 'html') {
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      return res;
    }

    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt_${reference || transactionId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return res;
  },

  downloadReceiptPdf: async ({ transactionId, reference }) => {
    return transactionService.downloadReceipt({ transactionId, reference, format: 'pdf' });
  },

  downloadStatementPdf: async ({ accountId, period = '3months', startDate, endDate, transactionType }) => {
    if (!accountId) throw new Error('accountId is required');

    const payload = {
      account_id: accountId,
      period,
    };
    if (startDate && endDate) {
      payload.start_date = startDate;
      payload.end_date = endDate;
    }
    if (transactionType) payload.transaction_type = transactionType;

    const res = await api.post('/transactions/statement_pdf/', payload, {
      responseType: 'blob',
    });

    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statement_${accountId}_${period}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Loan services
export const loanService = {
  getLoans: (params) => api.get('/loans/', { params }),
  getLoan: (id) => api.get(`/loans/${id}/`),
  applyLoan: (data) => api.post('/loans/', data),
  repayLoan: (id, data) => api.post(`/loans/${id}/repay/`, data),
  getLoanSchedule: (id) => api.get(`/loans/${id}/schedule/`),
  getLoanTypes: (params) => api.get('/loans/types/', { params }),
};

// Card Request services
export const cardRequestService = {
  getRequest:       ()     => api.get('/cards/request/'),
  getEligibility:   ()     => api.get('/cards/eligibility/'),
  saveRequest:      (data) => api.post('/cards/request/', data),
};

// Issued debit card services (ledger)
export const debitCardService = {
  getCards: (params) => api.get('/ledger/cards/', { params }),
  getCard:  (id)     => api.get(`/ledger/cards/${id}/`),
};

// Loan Application (new lifecycle) services
export const loanApplicationService = {
  getApplications: (params) => api.get('/loan-applications/', { params }),
  getApplication: (id) => api.get(`/loan-applications/${id}/`),
  createDraft: (data) => api.post('/loan-applications/', data),
  applyLoan: (data) => api.post('/loan-applications/applyLoan/', data),
  updateDraft: (id, data) => api.patch(`/loan-applications/${id}/`, data),
  submit: (id) => api.post(`/loan-applications/${id}/submit/`),
  getDocuments: (id) => api.get(`/loan-applications/${id}/documents/`),
  uploadDocument: (id, { type, file }) => {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('file', file);
    return api.post(`/loan-applications/${id}/documents/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  runKyc: (id) => api.post(`/loan-applications/${id}/run-kyc/`),
  validateDocs: (id) => api.post(`/loan-applications/${id}/validateDocs/`),
  runRisk: (id) => api.post(`/loan-applications/${id}/run-risk/`),
  riskScore: (id) => api.post(`/loan-applications/${id}/riskScore/`),
  approveLoan: (id, data = {}) => api.post(`/loan-applications/${id}/approveLoan/`, data),
  generateOffer: (id) => api.post(`/loan-applications/${id}/generate-offer/`),
  acceptOffer: (id, data) => api.post(`/loan-applications/${id}/accept-offer/`, data),
  disburseLoan: (id, data) => api.post(`/loan-applications/${id}/disburseLoan/`, data),
  withdraw: (id) => api.post(`/loan-applications/${id}/withdraw/`),
  getReceipt: (id, format = 'txt') => api.get(`/loan-applications/${id}/receipt/`, {
    params: { download_format: format },
    responseType: format === 'txt' ? 'blob' : 'json',
  }),
  getAudit: (id) => api.get(`/loan-applications/${id}/audit/`),
  getStatusCopy: (params) => api.get('/loan-applications/status-copy/', { params }),
};

// Bill services
export const billService = {
  getBills: (params) => api.get('/bills/', { params }),
  getBill: (id) => api.get(`/bills/${id}/`),
  createBill: (data) => api.post('/bills/', data),
  updateBill: (id, data) => api.patch(`/bills/${id}/`, data),
  deleteBill: (id) => api.delete(`/bills/${id}/`),
  payBill: (id, data) => api.post(`/bills/${id}/pay/`, data),
  getPayments: (params) => api.get('/bills/payments/', { params }),
};

// Order services
export const orderService = {
  getVendors: (params) => api.get('/orders/vendors/', { params }),
  getVendor: (id) => api.get(`/orders/vendors/${id}/`),
  getProducts: (params) => api.get('/orders/products/', { params }),
  getOrders: (params) => api.get('/orders/', { params }),
  getOrder: (id) => api.get(`/orders/${id}/`),
  createOrder: (data) => api.post('/orders/', data),
  payOrder: (id, data) => api.post(`/orders/${id}/pay/`, data),
  cancelOrder: (id) => api.post(`/orders/${id}/cancel/`),
};

// Support services (user tickets + staff inbox)
export const supportService = {
  listTickets: () => api.get('/support/tickets/'),
  createTicket: (data) => api.post('/support/tickets/', data),
  getTicket: (id) => api.get(`/support/tickets/${id}/`),
  listMessages: (ticketId) => api.get(`/support/tickets/${ticketId}/messages/`),
  sendMessage: (ticketId, data) => api.post(`/support/tickets/${ticketId}/messages/`, data),

  // Unread badge helper
  getUnreadCount: () => api.get('/support/tickets/unread_count/'),

  // Customer actions
  closeTicket: (ticketId) => api.post(`/support/tickets/${ticketId}/close/`, {}),
  reopenTicket: (ticketId) => api.post(`/support/tickets/${ticketId}/reopen/`, {}),

  // Staff-only helpers
  assignTicket: (ticketId, assigned_to) => api.post(`/support/tickets/${ticketId}/assign/`, { assigned_to }),
  setTicketStatus: (ticketId, status) => api.post(`/support/tickets/${ticketId}/set_status/`, { status }),
};

// FX (foreign exchange) services – bank-authorised currency conversion only
export const fxService = {
  /** All active multi-currency balances for the authenticated user. */
  getBalances: () => api.get('/ledger/fx/balances/'),

  /** Current FX rates. Optional base/quote query params to narrow results. */
  getRates: (params) => api.get('/ledger/fx/rates/', { params }),

  /** Rate for a specific currency pair. */
  getRate: (base, quote) => api.get(`/ledger/fx/rates/${base}/${quote}/`),

  /**
   * Admin-only: immediately pull live rates from the external provider.
   * Returns { detail, output }.
   */
  refreshRates: () => api.post('/ledger/fx/rates/refresh/', {}),

  /**
   * Preview a conversion: returns to_amount + rate without moving funds.
   * Body: { from_account_id, to_account_id, from_amount }
   */
  preview: (data) => api.post('/ledger/fx/preview/', data),

  /**
   * Execute the conversion.
   * Body: { from_account_id, to_account_id, from_amount, idempotency_key? }
   */
  convert: (data) => api.post('/ledger/fx/convert/', data),

  /** Paginated FX conversion history for the authenticated user. */
  getHistory: (params) => api.get('/ledger/fx/history/', { params }),
};

// Notification (ActivityLog) services
export const notificationService = {
  list:       (params = {}) => api.get('/notifications/', { params }),
  unreadCount: ()           => api.get('/notifications/unread-count/'),
  detail:     (id)          => api.get(`/notifications/${id}/`),
  markRead:   (id)          => api.post(`/notifications/${id}/read/`),
  markAllRead: ()           => api.post('/notifications/mark-all-read/'),
  archive:    (id)          => api.post(`/notifications/${id}/archive/`),
};

export default api;
